import type { MetaDescriptor, MetaFunction } from 'react-router';

/**
 * Merging helper
 *
 * {@link https://remix.run/docs/en/main/route/meta#meta-merging-helper}
 *
 * If you can't avoid the merge problem with global meta or index routes, we've created
 * a helper that you can put in your app that can override and append to parent meta easily.
 *
 * @example
 * ```typescript
 * import type { MetaFunction } from 'react-router';
 *
 * import { mergeMeta } from '~/utils/meta-utils';
 *
 * export const meta: MetaFunction<typeof loader> = mergeMeta(({ loaderData }) => {
 *   return [
 *     { title: "My Leaf Route" },
 *   ];
 * });
 *
 * // In a parent route:
 * import type { MetaFunction } from 'react-router';
 *
 * export const meta: MetaFunction<typeof loader> = ({ loaderData }) => {
 *   return [
 *     { title: "My Parent Route" },
 *     { name: 'description', content: "This is the parent route" },
 *   ];
 * }
 * ```
 * The resulting meta will contain both `title: 'My Leaf Route'` and `description: 'This is the parent route'`.
 */
export function mergeMeta<
  Loader = unknown,
  ParentsLoaders extends Record<string, unknown> = Record<string, unknown>
>(
  leafMetaFn: MetaFunction<Loader, ParentsLoaders>,
): MetaFunction<Loader, ParentsLoaders> {
  return (args) => {
    const leafMeta = leafMetaFn(args);

    return args.matches.reduceRight((acc, match) => {
      for (const parentMeta of match.meta) {
        addUniqueMeta(acc, parentMeta);
      }

      return acc;
    }, leafMeta);
  };
}

/**
 * Merging helper that works with Route Module Type Safety
 *
 * If you can't avoid the merge problem with global meta or index routes, we've created
 * a helper that you can put in your app that can override and append to parent meta easily.
 *
 * @example
 * ```typescript
 * import type { Route } from './+types/leaf';
 *
 * import { mergeRouteModuleMeta } from '~/utils/meta-utils';
 *
 * export const meta: Route.MetaFunction = mergeRouteModuleMeta(({ loaderData }) => {
 *   return [
 *     { title: "My Leaf Route" },
 *   ];
 * });
 *
 * // In a parent route:
 * import type { Route } from './+types/root';
 *
 * export const meta: Route.MetaFunction = ({ loaderData }) => {
 *   return [
 *     { title: "My Parent Route" },
 *     { name: 'description', content: "This is the parent route" },
 *   ];
 * }
 * ```
 * The resulting meta will contain both `title: 'My Leaf Route'` and `description: 'This is the parent route'`.
 */
export function mergeRouteModuleMeta(leafMetaFn: (args) => any): (args) => any {
  return (args) => {
    const leafMeta = leafMetaFn(args);

    let result: any[] = [];
    if (Array.isArray(leafMeta)) {
      result = [...leafMeta];
    } else if (leafMeta) {
      result = [leafMeta];
    }
    const matches = Array.isArray(args.matches) ? args.matches : [];

    return matches.reduceRight((acc, match) => {
      if (match && Array.isArray(match?.meta)) {
        for (const parentMeta of match.meta) {
          if (parentMeta) {
            addUniqueMeta(acc, parentMeta);
          }
        }
      }
      return acc;
    }, result);
  };
}

function addUniqueMeta(acc: MetaDescriptor[] | undefined, parentMeta: MetaDescriptor) {
  if (!acc) return;

  if (!acc.some(meta => isMetaEqual(meta, parentMeta))) {
    acc.push(parentMeta);
  }
}


function isMetaEqual(meta1: MetaDescriptor, meta2: MetaDescriptor): boolean {
  return ('name' in meta1 && 'name' in meta2 && meta1.name === meta2.name) ||
    ('property' in meta1 && 'property' in meta2 && meta1.property === meta2.property) ||
    ('title' in meta1 && 'title' in meta2) ||
    ('tagName' in meta1 && 'tagName' in meta2 && meta1.tagName === 'link' && meta2.tagName === 'link' &&
      'rel' in meta1 && 'rel' in meta2 && meta1.rel === meta2.rel &&
      (meta1.rel !== 'alternate' || ('hrefLang' in meta1 && 'hrefLang' in meta2 && meta1.hrefLang === meta2.hrefLang)));
}
